import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Save, Upload, Sparkles, Plus, Trash2 } from "lucide-react";

const EMPTY_FLIGHT = {
  flight_number: "", carrier: "", cabin: "Economy",
  from_airport: "", to_airport: "", from_terminal: "", to_terminal: "",
  depart: "", arrival: "",
  baggage: "", meal: "", status: "CONFIRMED", airline_ref: "",
};

const EMPTY_PASSENGER = { name: "", carrier: "", frequent_flyer: "", ticket_number: "" };

const EMPTY_FORM = {
  booking_id: null,
  booking_code: "",
  airline_ref: "",
  fare_rules: "",
  emergency_contact: "",
  additional_notes: "",
  passengers: [{ ...EMPTY_PASSENGER }],
  flights: [{ ...EMPTY_FLIGHT }],
};

export default function ETicketForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const prefill = localStorage.getItem("gg_eticket_prefill");
    if (prefill) {
      try {
        const p = JSON.parse(prefill);
        setForm((f) => ({
          ...f,
          booking_id: p.booking_id || null,
          booking_code: p.airline_ref || p.pnr || "",
          airline_ref: p.airline_ref || p.pnr || "",
          passengers: p.passenger ? [{ ...EMPTY_PASSENGER, name: p.passenger, carrier: p.airline || "", ticket_number: p.ticket_number || "" }] : [{ ...EMPTY_PASSENGER }],
          flights: [{
            ...EMPTY_FLIGHT,
            flight_number: p.flight_number || "",
            carrier: p.airline || "",
            cabin: p.cabin || "Economy",
            from_airport: (p.route || "").split(/-|→/)[0]?.trim() || "",
            to_airport: (p.route || "").split(/-|→/)[1]?.trim() || "",
            depart: p.depart || "",
            arrival: p.arrival || "",
            baggage: p.baggage || "",
            airline_ref: p.airline_ref || p.pnr || "",
          }],
        }));
      } catch {}
      localStorage.removeItem("gg_eticket_prefill");
    }
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFlight = (i, k, v) => setForm((f) => ({ ...f, flights: f.flights.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const setPax = (i, k, v) => setForm((f) => ({ ...f, passengers: f.passengers.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));

  const addFlight = () => setForm((f) => ({ ...f, flights: [...f.flights, { ...EMPTY_FLIGHT }] }));
  const removeFlight = (i) => setForm((f) => ({ ...f, flights: f.flights.filter((_, idx) => idx !== i) }));
  const addPax = () => setForm((f) => ({ ...f, passengers: [...f.passengers, { ...EMPTY_PASSENGER }] }));
  const removePax = (i) => setForm((f) => ({ ...f, passengers: f.passengers.filter((_, idx) => idx !== i) }));

  const onUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Harus PDF"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/etickets/parse", fd);

      const flights = (data.flights && data.flights.length ? data.flights : [{ ...EMPTY_FLIGHT }]).map((x) => ({ ...EMPTY_FLIGHT, ...x }));
      const passengers = (data.passengers && data.passengers.length ? data.passengers : [{ ...EMPTY_PASSENGER }]).map((x) => ({ ...EMPTY_PASSENGER, ...x }));

      setForm((f) => ({
        ...f,
        booking_code: data.booking_code || f.booking_code,
        airline_ref: data.airline_ref || flights[0]?.airline_ref || f.airline_ref,
        passengers,
        flights,
        original_ota_pdf_b64: data.original_ota_pdf_b64 || null,
        original_ota_filename: data.original_ota_filename || file.name,
      }));
      if (data.duplicates && data.duplicates.length) {
        toast.warning(`⚠️ Duplikat: PNR sudah pernah diterbitkan sebagai ${data.duplicates.map(d => d.eticket_number).join(", ")}`, { duration: 8000 });
      } else {
        toast.success(`AI berhasil ekstrak ${flights.length} penerbangan, ${passengers.length} penumpang`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal ekstrak PDF");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    const primary = form.flights[0] || {};
    const primaryPax = form.passengers[0] || {};
    const payload = {
      ...form,
      passenger: primaryPax.name || "",
      airline: primary.carrier || "",
      pnr: form.airline_ref || primary.airline_ref || "",
      route: [primary.from_airport, primary.to_airport].filter(Boolean).join(" - "),
      depart: primary.depart || "",
      arrival: primary.arrival || "",
      flight_number: primary.flight_number || "",
      cabin: primary.cabin || "Economy",
      baggage: primary.baggage || "",
      ticket_number: primaryPax.ticket_number || "",
    };
    try {
      const { data } = await api.post("/etickets", payload);
      setSaved(data);
      toast.success("E-ticket dibuat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const pdfUrl = saved ? `${process.env.REACT_APP_BACKEND_URL}/api/etickets/${saved.id}/pdf?token=${localStorage.getItem("gg_token")}` : "#";

  return (
    <div className="space-y-6" data-testid="eticket-form">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            {saved ? `E-ticket ${saved.eticket_number}` : "Generate E-ticket"}
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Upload PDF e-ticket dari OTA/airline — AI ekstrak, lalu terbitkan versi Gogojalan.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {saved && <a href={pdfUrl} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="eticket-pdf-btn"><Download size={14} className="mr-1"/> PDF</Button></a>}
          <Button onClick={save} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="eticket-save-btn"><Save size={14} className="mr-1"/> Save</Button>
        </div>
      </div>

      {/* Upload zone */}
      <Card className="border-[#e4ecc9]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sparkles size={18} className="text-[#6b8e23]"/> Upload E-ticket OTA (PDF)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <input ref={fileRef} type="file" accept="application/pdf" onChange={onUploadPdf} className="hidden" data-testid="eticket-pdf-upload"/>
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#556b2f] hover:bg-[#3d4d1e]">
              <Upload size={14} className="mr-2"/> {uploading ? "Mengekstrak..." : "Pilih PDF"}
            </Button>
            <p className="text-sm text-slate-500">
              PDF dari Agoda / Trip.com / Traveloka / Tiket.com / airline. Maks 10 MB. Multi-segment didukung.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Meta */}
      <Card>
        <CardHeader><CardTitle>Info Booking</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Booking Code (OTA)</Label><Input value={form.booking_code} onChange={(e) => setField("booking_code", e.target.value)} data-testid="et-booking-code"/></div>
          <div><Label>Airline Ref / PNR</Label><Input value={form.airline_ref} onChange={(e) => setField("airline_ref", e.target.value)} data-testid="et-airline-ref"/></div>
          <div><Label>Emergency Contact</Label><Input value={form.emergency_contact} onChange={(e) => setField("emergency_contact", e.target.value)} /></div>
          <div className="sm:col-span-3"><Label>Fare Rules</Label><Textarea rows={2} value={form.fare_rules} onChange={(e) => setField("fare_rules", e.target.value)}/></div>
          <div className="sm:col-span-3">
            <Label>Additional / Services (makanan, bagasi ekstra, seat, dll)</Label>
            <Textarea rows={2} value={form.additional_notes} onChange={(e) => setField("additional_notes", e.target.value)} placeholder="Contoh: Meal AVML pre-order, Bagasi tambahan 10kg, Seat 12A pre-book" data-testid="et-additional"/>
          </div>
        </CardContent>
      </Card>

      {/* Passengers */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Penumpang ({form.passengers.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={addPax} data-testid="add-pax-btn"><Plus size={14} className="mr-1"/> Tambah Penumpang</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.passengers.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="col-span-4"><Label>Nama</Label><Input value={p.name} onChange={(e) => setPax(i, "name", e.target.value)} data-testid={`pax-name-${i}`}/></div>
              <div className="col-span-3"><Label>Carrier</Label><Input value={p.carrier} onChange={(e) => setPax(i, "carrier", e.target.value)}/></div>
              <div className="col-span-2"><Label>Freq. Flyer</Label><Input value={p.frequent_flyer} onChange={(e) => setPax(i, "frequent_flyer", e.target.value)}/></div>
              <div className="col-span-2"><Label>Ticket #</Label><Input value={p.ticket_number} onChange={(e) => setPax(i, "ticket_number", e.target.value)}/></div>
              <div className="col-span-1">
                {form.passengers.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removePax(i)}><Trash2 size={14} className="text-red-600"/></Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Flights */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Segmen Penerbangan ({form.flights.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={addFlight} data-testid="add-flight-btn"><Plus size={14} className="mr-1"/> Tambah Segmen</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.flights.map((fl, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <div className="col-span-2"><Label>Flight #</Label><Input value={fl.flight_number} onChange={(e) => setFlight(i, "flight_number", e.target.value)} data-testid={`f-flight-${i}`}/></div>
              <div className="col-span-3"><Label>Carrier</Label><Input value={fl.carrier} onChange={(e) => setFlight(i, "carrier", e.target.value)}/></div>
              <div className="col-span-2"><Label>Class</Label><Input value={fl.cabin} onChange={(e) => setFlight(i, "cabin", e.target.value)}/></div>
              <div className="col-span-2"><Label>Baggage</Label><Input value={fl.baggage} onChange={(e) => setFlight(i, "baggage", e.target.value)} placeholder="20 kg"/></div>
              <div className="col-span-2"><Label>Airline Ref</Label><Input value={fl.airline_ref} onChange={(e) => setFlight(i, "airline_ref", e.target.value)}/></div>
              <div className="col-span-1 flex items-end">
                {form.flights.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeFlight(i)}><Trash2 size={14} className="text-red-600"/></Button>
                )}
              </div>

              <div className="col-span-4"><Label>From (airport)</Label><Input value={fl.from_airport} onChange={(e) => setFlight(i, "from_airport", e.target.value)} placeholder="Soekarno-Hatta (CGK)"/></div>
              <div className="col-span-4"><Label>To (airport)</Label><Input value={fl.to_airport} onChange={(e) => setFlight(i, "to_airport", e.target.value)} placeholder="Changi (SIN)"/></div>
              <div className="col-span-2"><Label>Depart</Label><Input value={fl.depart} onChange={(e) => setFlight(i, "depart", e.target.value)} placeholder="2026-08-15 08:30"/></div>
              <div className="col-span-2"><Label>Arrival</Label><Input value={fl.arrival} onChange={(e) => setFlight(i, "arrival", e.target.value)}/></div>

              <div className="col-span-3"><Label>Terminal Berangkat</Label><Input value={fl.from_terminal} onChange={(e) => setFlight(i, "from_terminal", e.target.value)} placeholder="Terminal 3"/></div>
              <div className="col-span-3"><Label>Terminal Tiba</Label><Input value={fl.to_terminal} onChange={(e) => setFlight(i, "to_terminal", e.target.value)} placeholder="Terminal 1"/></div>
              <div className="col-span-6"><Label>Meal / In-flight Service</Label><Input value={fl.meal} onChange={(e) => setFlight(i, "meal", e.target.value)} placeholder="Meal Included / AVML / Snack"/></div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
